import { useMemo } from "react";

import { FormRowField, FormikForm, LandingPageFieldText } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import { moduleSchema } from "@skilitics/nautilus-endpoint-definition";

import { FormBody, Header, Heading, PageContainer, StyledForm, SubHeading } from "../../shared";
import { useCreateModuleSettings, type CreateModuleSettings } from "../CreateModuleSettingsContext";

type FormValues = Pick<CreateModuleSettings["metadata"], "discipline" | "topic" | "focusPrimary" | "focusAdditional">;

const validationSchema = moduleSchema.pick({
  discipline: true,
  topic: true,
  focusPrimary: true,
  focusAdditional: true,
});

export const BriefAssistantPage = () => {
  const formatMessage = useFormatMessage(formatStrings);

  const { metadata, updateMetadata } = useCreateModuleSettings();

  const initialValues: FormValues = useMemo(
    () => ({
      discipline: metadata.discipline ?? "",
      topic: metadata.topic ?? "",
      focusPrimary: metadata.focusPrimary ?? "",
      focusAdditional: metadata.focusAdditional ?? "",
    }),
    [metadata.discipline, metadata.focusAdditional, metadata.focusPrimary, metadata.topic],
  );

  return (
    <PageContainer>
      <Header>
        <Heading>{formatMessage("heading")}</Heading>
        <SubHeading>{formatMessage("subHeading")}</SubHeading>
      </Header>
      <FormikForm<FormValues>
        initialValues={initialValues}
        onSubmit={updateMetadata}
        submitOnBlur
        formatStrings={formatStrings}
        validationSchema={validationSchema}
        dataTest="brief-assistant-form"
      >
        <StyledForm>
          <FormBody>
            <FormRowField<FormValues> name="discipline">
              <LandingPageFieldText />
            </FormRowField>

            <FormRowField<FormValues> name="topic">
              <LandingPageFieldText />
            </FormRowField>

            <FormRowField<FormValues> name="focusPrimary">
              <LandingPageFieldText />
            </FormRowField>

            <FormRowField<FormValues> name="focusAdditional">
              <LandingPageFieldText />
            </FormRowField>
          </FormBody>
        </StyledForm>
      </FormikForm>
    </PageContainer>
  );
};

const formatStrings = {
  heading: {
    en: "Working with AI",
  },
  subHeading: {
    en: "Let's brief your AI assistant on what you're building.",
  },
  disciplineLabel: {
    en: "The primary discipline this learning relates to is...",
  },
  disciplineDescription: {
    en: "The primary discipline.",
  },
  disciplinePlaceholder: {
    en: "Add a discipline...",
  },
  topicLabel: {
    en: "The specific area or topic within this discipline is?",
  },
  topicDescription: {
    en: "The specific area or topic.",
  },
  topicPlaceholder: {
    en: "Add a topic...",
  },
  focusPrimaryLabel: {
    en: "The primary focus of this module is...",
  },
  focusPrimaryDescription: {
    en: "The primary focus.",
  },
  focusPrimaryPlaceholder: {
    en: "Add a primary focus...",
  },
  focusAdditionalLabel: {
    en: "Additional areas included are...",
  },
  focusAdditionalDescription: {
    en: "The additional areas.",
  },
  focusAdditionalPlaceholder: {
    en: "Add an additional focus...",
  },
} satisfies FormatMessages;
